import * as React from 'react';
import PropTypes from 'prop-types';
import { Grid, CssBaseline, withStyles, Typography, MuiThemeProvider, createMuiTheme } from '@material-ui/core';

import { Switch, Route, Redirect } from 'react-router-dom';
import { PrivateRoute } from '../common';
import { Routes } from './App.routes';
import OnBoarding from '../OnBoarding';

import './App.css';
import AppHeaderFooter from '../AppHeaderFooter';
import StudentRoster from '../StudentRoster';
import packageJson from '../../../package.json';
import AtoF from '../AtoF';
import Settings from '../Settings';
import AccountabilityWeight from '../AccountabilityWeight';
import ProgressGoals from '../ProgressGoals';
import CCMRTracking from '../CCMRTracking';
import HB1416Tracker from '../HB1416Tracker';
import AcceleratedTesters from '../AcceleratedTesters';
import OutcomesBonus from '../OutcomesBonus';
import AssessmentManager from '../Assessments';
import HomePage from '../HomePage';
import { SignIn, ForgotPassword, SignUp } from '../Auth';
import FlightPlan from '../FlightPlan';

// Demo facelift gate. When REACT_APP_THEME=demo the app gets the "Polished & Warm,
// compact" look; otherwise every value below collapses to the original prod theme so
// the live connect.copilotdata.com build stays byte-for-byte identical.
const isDemo = process.env.REACT_APP_THEME === 'demo';

// Flip the global font variable defined in App.css (--app-font). The `* { font-family }`
// rule outranks MUI's typography injection, so the swap has to happen here too. Prod
// leaves the variable on its CSS default (Questrial).
if (isDemo && typeof document !== 'undefined') {
  document.documentElement.style.setProperty('--app-font', "'Source Sans Pro', sans-serif");
}

const palette = {
  primary: {
    main: '#656565',
    light: '#C4C4C4',
    contrastText: '#fff',
  },
  secondary: {
    main: '#BFF7FF',
    light: '#E6FBFE',
    contrastText: '#fff',
  },
  warning: {
    main: '#DFC700',
    contrastText: '#fff',
  },
  error: {
    main: '#EE9696',
    dark: '#FF5656',
    contrastText: '#fff',
  },
  success: {
    main: '#68D190',
    light: '#E1F5E9',
  },
  inactive: '#E2E2E2',
  white: '#fff',
};

// Demo-only component overrides. Scoped deliberately: padding/borders for the clean MUI
// tables, rounded + soft cyan-shadow panels, non-uppercase buttons. NO MuiTableHead
// (sticky headers are per-component) and NO button padding/min-height (in-cell buttons
// use padding:0). Dense domain tables that use higher-specificity withStyles classes are
// compacted in their own files.
const demoOverrides = {
  MuiTableCell: {
    root: { padding: '3px 10px', borderBottom: '1px solid #ECECEC' },
    head: { padding: '4px 8px', color: '#656565', fontWeight: 600, fontSize: '0.75rem' },
    body: { fontSize: '0.8125rem' },
    sizeSmall: { padding: '3px 10px' },
  },
  MuiTableRow: {
    root: { height: 'auto' },
  },
  MuiTableBody: {
    root: {
      // Row hover only — the alternating-row stripe fought the subject band colors on the
      // A-F/Domain-3 reports, so it's dropped in favor of structure (the section cards).
      '& tr:hover': { backgroundColor: '#f6fdff', transition: 'background-color .15s' },
    },
  },
  MuiPaper: {
    rounded: { borderRadius: 8 },
    elevation1: { boxShadow: '0 2px 10px rgba(134, 240, 255, 0.18), 0 1px 3px rgba(0, 0, 0, 0.06)' },
  },
  MuiButton: {
    root: { textTransform: 'none', borderRadius: 8 },
    label: { textTransform: 'none' },
    contained: { boxShadow: 'none' },
  },
};

const theme = createMuiTheme({
  typography: {
    useNextVariants: true,
    fontFamily: isDemo ? ['"Source Sans Pro"', 'sans-serif'].join(',') : ['"Questrial"', 'sans-serif'].join(','),
  },
  palette,
  ...(isDemo ? { overrides: demoOverrides } : {}),
});

const styles = (theme) => ({
  root: {
    height: '100%',
    background: 'white',
    position: 'relative',
  },
  content: {
    margin: 'auto',
  },
});

class App extends React.PureComponent {
  static propTypes = {
    appInit: PropTypes.func.isRequired,
    appTeardown: PropTypes.func.isRequired,
    classes: PropTypes.any.isRequired,
    user: PropTypes.object,
    history: PropTypes.object.isRequired,
    redirect: PropTypes.string.isRequired,
    doRedirect: PropTypes.func.isRequired,
    profile: PropTypes.object,
    districtAdvancedSettings: PropTypes.object,
  };

  componentDidMount() {
    const { appInit } = this.props;
    appInit();
  }

  componentDidUpdate() {
    const { redirect, history, doRedirect } = this.props;
    if (redirect) {
      doRedirect(history, redirect);
    }
  }

  componentWillUnmount() {
    const { appTeardown } = this.props;
    appTeardown();
  }

  renderApp = (props) => {
    const { profile, districtAdvancedSettings } = this.props;
    let districtMenusVisibility = {};
    if (profile && districtAdvancedSettings) {
      if (districtAdvancedSettings.menusVisibility) {
        districtMenusVisibility = districtAdvancedSettings.menusVisibility;
      }
    }

    return (
      <AppHeaderFooter {...props}>
        <Switch>
          {districtMenusVisibility.progressGoals != false && (
            <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.staar}`} Component={ProgressGoals} />
          )}
          {districtMenusVisibility.weights != false && (
            <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.weights}`} Component={AccountabilityWeight} />
          )}
          <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.studentRoster}`} Component={StudentRoster} />

          {districtMenusVisibility.reports != false &&
            profile &&
            (profile.isSuperAdmin === true || profile.canViewAccountabilityReports === true) && (
              <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.aToF}`} Component={AtoF} />
            )}

          {districtMenusVisibility.flightPlan != false &&
            profile &&
            (profile.isSuperAdmin === true || profile.canViewAccountabilityReports === true) && (
              <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.flightPlan}`} Component={FlightPlan} />
            )}
          <Redirect from={`/${Routes.settings}`} to={`/${Routes.settings}/account`} exact />
          <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.settings}/:type`} Component={Settings} />
          <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.assessments}`} Component={AssessmentManager} />
          {districtMenusVisibility.ccmrTracking != false && (
            <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.ccmrTracking}`} Component={CCMRTracking} />
          )}
          {districtMenusVisibility.hb1416Tracker != false &&
            profile &&
            profile.featureFlags &&
            profile.featureFlags.hb1416Tracker === true && (
              <PrivateRoute
                redirect={`/${Routes.signIn}`}
                path={`/${Routes.hb1416Tracker}`}
                Component={HB1416Tracker}
              />
            )}
          {districtMenusVisibility.acceleratedTesters != false && (
            <PrivateRoute
              redirect={`/${Routes.signIn}`}
              path={`/${Routes.acceleratedTesters}`}
              Component={AcceleratedTesters}
            />
          )}
          {districtMenusVisibility.outcomesBonus != false &&
            profile &&
            profile.featureFlags &&
            profile.featureFlags.outcomesBonus === true && (
              <PrivateRoute
                redirect={`/${Routes.signIn}`}
                path={`/${Routes.outcomesBonus}`}
                Component={OutcomesBonus}
              />
            )}
          <PrivateRoute redirect={`/${Routes.signIn}`} exact path="" Component={HomePage} />
          <Route path={`/${Routes.versionNumber}`} render={this.renderVersionNumber} />
        </Switch>
      </AppHeaderFooter>
    );
  };

  renderVersionNumber = () => (
    <Grid container justify="center" alignItems="center">
      <div>
        <Typography variant="h1">{packageJson.version}</Typography>
        <Typography variant="subtitle1" align="center" style={{ color: '#c1c1c1' }}>
          {process.env.REACT_APP_GIT_SHORT_SHA}
        </Typography>
      </div>
    </Grid>
  );

  render() {
    const { classes } = this.props;
    return (
      <MuiThemeProvider theme={theme}>
        <Grid container className={classes.root}>
          <CssBaseline />
          <Switch>
            <PrivateRoute redirect={`/${Routes.signIn}`} path={`/${Routes.onboarding}/:page?`} Component={OnBoarding} />
            <Route exact path={`/${Routes.forgotPassword}`} component={ForgotPassword} />
            <Route exact path={`/${Routes.signIn}`} component={SignIn} />
            <Route exact path={`/${Routes.signUp}/:code?`} component={SignUp} />
            <Route exact path="" strict={false} render={this.renderApp} />
          </Switch>
        </Grid>
      </MuiThemeProvider>
    );
  }
}

export default withStyles(styles)(App);
